import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * SlotPickerModal — Shared slot picker for individual lesson booking.
 * Used by both DirectoryLessonCard and LessonCarouselCard.
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {Function} onClose - Called when modal should close
 * @param {Function} onConfirm - Called with (slotIndex) when user confirms
 * @param {Array} slots - Array of { startTime, status } objects
 * @param {string} date - Formatted display date string
 * @param {number} duration - Lesson duration in minutes
 * @param {number} price - Price in USD
 * @param {Function} formatSlotTime - Converts 24h time string to 12h AM/PM
 */
const SlotPickerModal = ({
  isOpen,
  onClose,
  onConfirm,
  slots = [],
  date,
  duration,
  price,
  formatSlotTime,
}) => {
  const [selectedSlotIndex, setSelectedSlotIndex] = useState(null);

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) setSelectedSlotIndex(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (selectedSlotIndex === null) return;
    onConfirm(selectedSlotIndex);
  };

  return (
    <>
      <div className="spm-overlay" onClick={onClose}>
        <div className="spm-modal" onClick={(e) => e.stopPropagation()}>
          <button className="spm-close-btn" onClick={onClose}>
            <X size={20} />
          </button>

          <h3 className="spm-title">Select a Time</h3>
          <p className="spm-subtitle">
            {date} &bull; {duration} min &bull; ${price}
          </p>

          <div className="spm-slot-grid">
            {slots.map((slot, idx) => {
              const isAvailable = slot.status !== 'booked';
              const isSelected = selectedSlotIndex === idx;

              return (
                <button
                  key={idx}
                  type="button"
                  className={`spm-slot${isSelected ? ' spm-slot-selected' : ''}${!isAvailable ? ' spm-slot-unavailable' : ''}`}
                  onClick={() => isAvailable && setSelectedSlotIndex(idx)}
                  disabled={!isAvailable}
                >
                  <span className="spm-slot-time">{formatSlotTime(slot.startTime)}</span>
                  {!isAvailable && <span className="spm-slot-status">Booked</span>}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className="spm-confirm-btn"
            onClick={handleConfirm}
            disabled={selectedSlotIndex === null}
          >
            Continue to Checkout
          </button>
        </div>
      </div>

      <style>{`
        .spm-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .spm-modal {
          background: #1a1b2e;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          padding: 32px;
          max-width: 400px;
          width: 100%;
          position: relative;
        }

        .spm-close-btn {
          position: absolute;
          top: 16px;
          right: 16px;
          background: transparent;
          border: none;
          color: #7a8599;
          cursor: pointer;
          padding: 4px;
        }

        .spm-close-btn:hover {
          color: #e2e8f0;
        }

        .spm-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #e2e8f0;
          margin-bottom: 8px;
        }

        .spm-subtitle {
          font-size: 0.9rem;
          color: #7a8599;
          margin-bottom: 24px;
        }

        .spm-slot-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
          margin-bottom: 24px;
        }

        .spm-slot {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 12px;
          background: rgba(255, 255, 255, 0.04);
          border: 2px solid rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .spm-slot:hover:not(:disabled) {
          border-color: #fbbf24;
        }

        .spm-slot-selected {
          border-color: #fbbf24;
          background: rgba(251, 191, 36, 0.15);
        }

        .spm-slot-unavailable {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .spm-slot-time {
          font-weight: 600;
          color: #e2e8f0;
        }

        .spm-slot-status {
          font-size: 0.75rem;
          color: #7a8599;
          margin-top: 4px;
        }

        .spm-confirm-btn {
          width: 100%;
          padding: 14px 20px;
          background: #fbbf24;
          color: #1a1a2e;
          border: none;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .spm-confirm-btn:hover:not(:disabled) {
          background: #f59e0b;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(251, 191, 36, 0.4);
        }

        .spm-confirm-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @media (max-width: 480px) {
          .spm-modal {
            padding: 24px;
          }

          .spm-slot-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  );
};

export default SlotPickerModal;
